<template>
  <div>
    <template v-if="iconOnly">
      <v-tooltip location="top">
        <template v-slot:activator="{ props: tooltipProps }">
          <v-btn icon variant="text" v-bind="tooltipProps" @click="openDialog">
            <v-icon>mdi-translate</v-icon>
          </v-btn>
        </template>
        <div v-if="translations.length === 0">{{ title }}</div>
        <div v-else>
          <div v-for="t in tooltipTranslations" :key="t.langCode">{{ t.langCode }}: {{ t.name }}</div>
          <div v-if="translations.length > 10">...</div>
        </div>
      </v-tooltip>
    </template>
    <template v-else>
      <v-btn variant="outlined" @click="openDialog">{{ title }}</v-btn>
    </template>

    <v-dialog v-model="dialogOpen" max-width="1200px" persistent>
      <v-card>
        <v-toolbar color="surface-variant">
          <v-toolbar-title>{{ title }}</v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn icon @click="cancel">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-toolbar>

        <v-card-text>
          <v-form ref="formRef">
            <v-container>
              <v-row v-for="(translation, index) in localTranslations" :key="index">
                <v-col cols="12" md="5">
                  <v-select
                    density="compact"
                    :items="translationLanguages"
                    v-model="translation.langCode"
                    item-value="lang"
                    item-title="description"
                    :label="$t('component-languages.edit.lang')"
                    :rules="[v => !!v || $t('component-languages.validation.langRequired'), langUniqueRule(translation)]"
                  ></v-select>
                </v-col>
                <v-col cols="12" md="5">
                  <v-text-field
                    density="compact"
                    :label="$t('component-languages.edit.name')"
                    v-model="translation.name"
                    :rules="[v => !!v || $t('component-languages.validation.nameRequired')]"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="2" class="d-flex align-center">
                  <v-btn icon variant="text" @click="removeLang(translation)">
                    <v-icon>mdi-delete</v-icon>
                  </v-btn>
                </v-col>
              </v-row>
            </v-container>
          </v-form>
        </v-card-text>

        <v-card-actions>
          <v-btn variant="outlined" @click="addLangRow">{{ $t('component-languages.edit.add') }}</v-btn>
          <v-spacer></v-spacer>
          <v-btn variant="outlined" @click="cancel">{{ $t('component-languages.edit.cancel') }}</v-btn>
          <v-btn variant="elevated" color="primary" @click="save">{{ $t('component-languages.edit.save') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue';
import type { LanguageTranslationValue } from '@/venit/services/model/techpubs-cms';
import { useTranslationStore } from '@/stores';
import { useI18n } from '@/shared/composables/i18n';

const props = withDefaults(defineProps<{
  translations: LanguageTranslationValue[];
  title: string;
  iconOnly?: boolean;
}>(), {
  iconOnly: false,
});

const emit = defineEmits<{
  'update:translations': [value: LanguageTranslationValue[]];
}>();

const { t: $t } = useI18n();
const translationStore = useTranslationStore();
const translationLanguages = computed(() => translationStore.translationLanguages);

const tooltipTranslations = computed(() =>
  [...props.translations].sort((a, b) => a.langCode.localeCompare(b.langCode)).slice(0, 10)
);

const langUniqueRule = (translation: LanguageTranslationValue) => (v: string) =>
  !v || localTranslations.value.filter(t => t.langCode === v).length <= 1 || $t('component-languages.validation.langDuplicate');

const dialogOpen = ref(false);
const localTranslations = ref<LanguageTranslationValue[]>([]);
const formRef = ref<{ validate: () => Promise<{ valid: boolean }> } | null>(null);

const openDialog = () => {
  localTranslations.value = props.translations.map(t => ({ ...t }));
  dialogOpen.value = true;
};

const cancel = () => {
  dialogOpen.value = false;
};

const save = async () => {
  const { valid } = await formRef.value?.validate() ?? { valid: true };
  if (!valid) return;
  emit('update:translations', localTranslations.value);
  dialogOpen.value = false;
};

const removeLang = (selectedTranslation: LanguageTranslationValue) => {
  localTranslations.value = localTranslations.value.filter(t => t !== selectedTranslation);
};

const addLangRow = () => {
  localTranslations.value = [...localTranslations.value, { langCode: '', name: '' }];
};
</script>
